import React, { useState } from 'react';
import { HardwareType, ModelReferenceCreate } from '../../services/typesService';
import { useI18n } from '../../contexts/I18nContext';

interface AddModelReferenceModalProps {
  isOpen: boolean;
  hardwareTypes: HardwareType[];
  onClose: () => void;
  onCreated: () => void;
  onCreate: (hardwareTypeId: number, data: ModelReferenceCreate) => Promise<void>;
}

interface FormData {
  hardwareTypeId: number;
  code: string;
  name: string;
  description: string;
}

interface FormErrors {
  [key: string]: string;
}

/** A new model reference: the hardware type it revises, its code and name. */
export const AddModelReferenceModal: React.FC<AddModelReferenceModalProps> = ({
  isOpen,
  hardwareTypes,
  onClose,
  onCreated,
  onCreate
}) => {
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({ hardwareTypeId: 0, code: '', name: '', description: '' });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.hardwareTypeId) newErrors.hardwareTypeId = t('deviceTypes.validation.hardwareTypeRequired');
    if (!formData.code.trim()) newErrors.code = t('deviceTypes.validation.refCodeRequired');
    else if (!/^[A-Z0-9-]{2,20}$/.test(formData.code)) newErrors.code = t('deviceTypes.validation.refCodeFormat');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await onCreate(formData.hardwareTypeId, {
        code: formData.code.trim(),
        name: formData.name.trim() || `${formData.code.trim()} Revision`,
        description: formData.description.trim() || undefined
      });
      onCreated();
      resetForm();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || (error instanceof Error ? error.message : t('deviceTypes.modelRef.failed'));
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ hardwareTypeId: 0, code: '', name: '', description: '' });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const activeHardwareTypes = hardwareTypes.filter(ht => ht.is_active);
  const chosen = hardwareTypes.find((ht) => ht.id === formData.hardwareTypeId);
  const summaryReady = Boolean(chosen && formData.code.trim());

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="config-template-modal tpc dtm" data-testid="modal-add-model-reference" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <div className="tpd-title-text">
              <h2>{t('deviceTypes.addModelReference')}</h2>
              <div className="tpd-title-sub"><span>{t('deviceTypes.modelRef.subtitle')}</span></div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={handleClose} aria-label={t('common.close')}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="tpc-form">
          <div className="modal-content tpd-body">
            {errors.submit && <div className="error-message" role="alert">{errors.submit}</div>}

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('deviceTypes.hardwareType')}</h3>
                <p>{t('deviceTypes.modelRef.hardwareIntro')}</p>
              </header>
              <div className="cu-field">
                <label htmlFor="mrType">{t('deviceTypes.hardwareType')} *</label>
                <select
                  id="mrType"
                  value={formData.hardwareTypeId || ''}
                  data-testid="field-modelref-hardware-type"
                  onChange={(e) => handleInputChange('hardwareTypeId', Number(e.target.value))}
                  className={`cu-input${errors.hardwareTypeId ? ' is-invalid' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value="">{t('configTemplates.selectHardwareTypePlaceholder')}</option>
                  {activeHardwareTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name} ({type.code})</option>
                  ))}
                </select>
                {errors.hardwareTypeId && <span className="cu-error">{errors.hardwareTypeId}</span>}
              </div>
            </section>

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('deviceTypes.referenceDetails')}</h3>
                <p>{t('deviceTypes.modelRef.detailsIntro')}</p>
              </header>
              <div className="cu-grid">
                <div className="cu-field">
                  <label htmlFor="mrCode">{t('deviceTypes.referenceCode')} *</label>
                  <input
                    type="text"
                    id="mrCode"
                    data-testid="field-modelref-code"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    className={`cu-input mono${errors.code ? ' is-invalid' : ''}`}
                    placeholder={t('deviceTypes.modelRef.codePlaceholder')}
                    maxLength={20}
                    disabled={isSubmitting}
                  />
                  {errors.code ? <span className="cu-error">{errors.code}</span> : <span className="cu-hint">{t('deviceTypes.modelRef.codeHint')}</span>}
                </div>
                <div className="cu-field">
                  <label htmlFor="mrName">{t('deviceTypes.name')}</label>
                  <input
                    type="text"
                    id="mrName"
                    data-testid="field-modelref-name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="cu-input"
                    placeholder={t('deviceTypes.modelRef.namePlaceholder')}
                    disabled={isSubmitting}
                  />
                  <span className="cu-hint">{t('deviceTypes.modelRef.nameHint')}</span>
                </div>
                <div className="cu-field cu-field-wide">
                  <label htmlFor="mrDescription">{t('common.description')}</label>
                  <textarea
                    id="mrDescription"
                    value={formData.description}
                    data-testid="field-modelref-description"
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="cu-input"
                    placeholder={t('deviceTypes.modelRefDescriptionPlaceholder')}
                    rows={2}
                    disabled={isSubmitting}
                  />
                  <span className="cu-hint">{t('deviceTypes.modelRef.descriptionHint')}</span>
                </div>
              </div>
            </section>
          </div>

          <div className="modal-actions tpd-footer tpc-footer">
            <div className="tpc-summary">
              {summaryReady && chosen ? (
                <>
                  <strong>{formData.code}</strong>
                  <span className="type-badge tpl-type">{chosen.code}</span>
                  <span className="tpc-summary-owner">{chosen.name}</span>
                </>
              ) : (
                <span className="tpd-muted">{t('deviceTypes.modelRef.summaryEmpty')}</span>
              )}
            </div>
            <div className="tpc-actions">
              <button type="button" onClick={handleClose} className="btn btn-secondary" data-testid="btn-cancel" disabled={isSubmitting}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={isSubmitting}>
                {isSubmitting ? t('deviceTypes.modelRef.creating') : t('deviceTypes.modelRef.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
